import { useCallback, useMemo, useState } from "react";
import * as XLSX from "xlsx";

import TablePage from "./TablePage";

function CustomersPage({ config, onRowClick }) {
  const rows = useMemo(() => config?.rows || [], [config?.rows]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [query, setQuery] = useState("");

  const formatFilename = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = now.toLocaleString("en", { month: "short" });
    const year = String(now.getFullYear()).slice(-2);
    return `customers_${day}${month}${year}`;
  };

  const normalizeRow = useCallback((row) => {
    const customer = row?.customer || {};
    const contact = customer.contact || {};
    const item = row?.item || {};
    return {
      Name: customer.name || item.title || "Untitled",
      Phone: contact.phone || row?.phone || "",
      Email: contact.email || row?.email || "",
      Address: customer.address || item.subtitle || "",
      Notes: customer.notes || "",
    };
  }, []);

  const handleExport = useCallback(
    (format = "xlsx") => {
      const data = rows.map(normalizeRow);
      const sheet = XLSX.utils.json_to_sheet(data);
      const filename = `${formatFilename()}.${format === "csv" ? "csv" : "xlsx"}`;

      if (format === "csv") {
        const csv = XLSX.utils.sheet_to_csv(sheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Customers");
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
    [normalizeRow, rows]
  );

  const matchesQuery = useCallback(
    (row) => {
      const value = (query || "").trim().toLowerCase();
      const numericQuery = (query || "").replace(/\D+/g, "");
      if (!value && !numericQuery) return true;

      const customer = row?.customer || {};
      const contact = customer.contact || row?.contact || {};
      const item = row?.item || {};

      const candidates = [
        customer.name,
        item.title,
        item.subtitle,
        row?.name,
        row?.phone || contact.phone,
        row?.email || contact.email,
      ].filter(Boolean);

      return candidates.some((entry) => {
        const text = String(entry).toLowerCase();
        const digits = text.replace(/\D+/g, "");
        return (value && text.includes(value)) || (numericQuery && digits && digits.includes(numericQuery));
      });
    },
    [query]
  );

  const filteredRows = useMemo(() => rows.filter(matchesQuery), [rows, matchesQuery]);

  const renderSearchBar = useCallback(
    () => (
      <div className="table-search">
        <div className="table-search-inner">
          <span className="search-icon" aria-hidden="true">
            🔍
          </span>
          <input
            type="search"
            placeholder="Search by name, phone, or email"
            aria-label="Search customers by name, phone, or email"
            value={query}
            onChange={(event) => setQuery(event?.target?.value || "")}
          />
        </div>
      </div>
    ),
    [query]
  );

  const renderExportActions = () => (
    <div className="export-menu" style={{ position: "relative" }}>
      <button className="ghost-btn small" type="button" onClick={() => setShowExportMenu((prev) => !prev)}>
        Export ▾
      </button>
      {showExportMenu ? (
        <div
          className="panel menu-card"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            padding: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            zIndex: 5,
            minWidth: "160px",
          }}
        >
          <button
            type="button"
            className="ghost-btn subtle wide"
            onClick={() => {
              handleExport("xlsx");
              setShowExportMenu(false);
            }}
          >
            Excel (.xlsx)
          </button>
          <button
            type="button"
            className="ghost-btn subtle wide"
            onClick={() => {
              handleExport("csv");
              setShowExportMenu(false);
            }}
          >
            CSV (.csv)
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <TablePage
      config={config}
      onRowClick={onRowClick}
      hideViewAction
      hideQuickActions
      rows={filteredRows}
      renderSearch={renderSearchBar}
      renderActions={renderExportActions}
      actionsRightAligned
    />
  );
}

export default CustomersPage;
